import React, {useRef, useState, useEffect} from 'react';
import {View, Text, StyleSheet, TouchableOpacity} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import OrderItemSuggested from '../../commons/orderItemSuggested';
import DisplaySerialRecordingNote from '../../commons/displaySerialRecordingNote';
import DisplayInstructions from '../../commons/displayOrderInstructions';
import DisplayLiveStats from '../../commons/displayLiveStats';
import SuggestedMenuMobile from '../scanpack/suggestedMenuMobile';
import placeholder from '../../../assets/placeholder.jpg';
import DisplaySuggestedImageDesktop from '../scanpack/displaySuggestedImageDesktop';
import DisplayImage from '../scanpack/displaySuggestedImage';
import commonStyles from '../../style/commonStyles';
import globalStyles from '../../style/global';
import {getFontSize, getWidth, getMargin} from '../scanToCartScanning/utils/responsiveUtils';
import useWidth from '../scanToCartScanning/useWidth';
import { redirectToProductDetail, openAddBarcodeWindow, handleReloadOrder } from '../../actions/scanpackScanningActions';
import { SwapUPC } from '../../actions/menuActions';
import { useSpeakLocation } from './hooks/useSpeakOnItemChange';
import { getItemCounts } from './utils/itemQuantities';

const ATTRIBUTE_ROWS = [
  {label: 'L1', valueKey: 'location', flagKey: 'display_location'},
  {label: 'L2', valueKey: 'location2', flagKey: 'display_location2'},
  {label: 'L3', valueKey: 'location3', flagKey: 'display_location3'},
  {label: 'C1', valueKey: 'custom_product_1', flagKey: 'custom_product_display_1'},
  {label: 'C2', valueKey: 'custom_product_2', flagKey: 'custom_product_display_2'},
  {label: 'C3', valueKey: 'custom_product_3', flagKey: 'custom_product_display_3'}
];

const hasValue = value => value !== undefined && value !== null && value !== '';

const SuggestedItemDisplay = () => {
  const windowWidth = useWidth();
  const currentItem = useSelector(state => state.scanPackScanning.currentItem);
  const order = useSelector(state => state.scanPackScanning.currentOrder);
  const scanPackSettings = useSelector(state => state.user?.bothSettings?.data?.data?.scanpack_setting);
  const dispatch = useDispatch();
  const speakLocation = useSpeakLocation();
  const isSwap = useSelector(state => state.menu?.isSwap);
  const removeOrderItems = useSelector(state => state.order?.removeOrderItems);
  const realTimeStatsEnabled = useSelector(
    state => state.user.bothSettings?.data?.data?.general_setting?.allow_rts
  );
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const removePendingRef = useRef(false);
  const isMobile = windowWidth <= 900;

  useEffect(() => {
    if (removePendingRef.current && removeOrderItems) {
      removePendingRef.current = false;
      dispatch(handleReloadOrder());
    }
  }, [removeOrderItems]);

  const onItemPress = (item) => {
    dispatch(redirectToProductDetail(item.product_id));
  };

  const createLocationRow = (key, label, value, backgroundColor) => (
    <TouchableOpacity
      key={key}
      activeOpacity={0.7}
      onPress={() => speakLocation(value)}
      style={[componentStyles.locationRow, {backgroundColor}]}>
      <Text style={componentStyles.locationLabel}>
        {label} :
      </Text>
      <Text style={componentStyles.locationValue}>
        {value}
      </Text>
    </TouchableOpacity>
  );

  const getAttributeRows = () => {
    if (!currentItem) return [];

    return ATTRIBUTE_ROWS
      .filter(({valueKey, flagKey}) => hasValue(currentItem[valueKey]) && (currentItem[flagKey] || scanPackSettings?.[flagKey]))
      .map(({label, valueKey}, index) =>
        createLocationRow(label, label, currentItem[valueKey], index % 2 === 0 ? '#b6cadd' : '#768da5')
      );
  };

  const renderSwapToggle = () => (
    <TouchableOpacity
      testID="swapLocationBarcode"
      onPress={() => dispatch(SwapUPC(!isSwap))}
      style={componentStyles.swapToggle}>
      <i
        className="icon-updown-circle"
        style={{color: isSwap ? '#000' : '#7c95ae', fontSize: '20px'}}
      />
    </TouchableOpacity>
  );

  const getDisplayImageProps = () => ({
    source: placeholder,
    defaultSource: placeholder,
    scannedQty: getItemCounts(currentItem).scanned,
    totalQty: getItemCounts(currentItem).total,
    remainingQty: getItemCounts(currentItem).remaining,
    images: currentItem?.images || [],
    id: order?.id,
    order: order,
    getWidth: () => getWidth(windowWidth),
    getMargin: () => getMargin(windowWidth)
  });

  const renderDisplayImage = () => {
    const props = getDisplayImageProps();

    if (windowWidth > 900) {
      return <DisplaySuggestedImageDesktop {...props} onPressGetOrderData={() => dispatch(handleReloadOrder())} />;
    }

    return (
      <DisplayImage
        {...props}
        scanned_qty={props.scannedQty}
        total_qty={props.totalQty}
        updateShowMenu={() => setShowMobileMenu(true)}
      />
    );
  };

  if (!currentItem) {
    return (
      <View style={componentStyles.emptyStateContainer}>
        <Text style={componentStyles.emptyStateText}>
          No items to scan
        </Text>
      </View>
    );
  }

  const firstUnscanned = order?.unscanned_items?.[0];
  const isKitChild = firstUnscanned?.product_type === 'individual' && firstUnscanned?.child_items?.length > 0;
  const serialNoteName = isKitChild ? 'serialRecordingNoteChild' : 'serialRecordingNoteSingle';

  // L1 (always show the lot + that lot's on-hand quantity) and the Z1-Z3 zero/negative visual
  // cue. `wms_inventory` is present only when WMS is on for both the tenant and this product's
  // warehouse (Groovepacker::Inventory::LotSnapshot.for_scan) — its absence means "WMS off",
  // which keeps this block a no-op for the vast majority of tenants. L1 is unconditional (no
  // setting hides it); the red cue fires whenever the ledger shows zero or negative for the
  // PRODUCT overall, regardless of which Z rung is configured — Z1/Z2/Z3 all render the same
  // cue, they differ only in whether a stop-and-receive prompt also interrupts (Z3, not yet
  // wired here).
  const wmsInventory = currentItem?.wms_inventory;
  const wmsIsZero = wmsInventory && Number(wmsInventory.quantity_on_hand) <= 0;

  const attributeRows = getAttributeRows();
  const swapped = isMobile && isSwap;
  const rowsWithSwapToggle = isMobile && currentItem.location ? (
    <>
      {attributeRows}
      {renderSwapToggle()}
    </>
  ) : attributeRows;

  return (
    <>
      {isMobile && showMobileMenu && (
        <View testID="mobileImageMenuOverlay" style={componentStyles.mobileMenuOverlay}>
          <SuggestedMenuMobile
            order={{order, id: order?.id}}
            updateShowMenu={() => setShowMobileMenu(false)}
            updateIsRemove={() => { removePendingRef.current = true; }}
          />
        </View>
      )}
      <DisplaySerialRecordingNote item={currentItem} name={serialNoteName} />
      {wmsInventory ? (
        <View
          testID="wmsLotDisplay"
          style={[componentStyles.wmsLotRow, wmsIsZero && componentStyles.wmsLotRowZero]}
        >
          <Text style={componentStyles.wmsLotText}>
            {wmsInventory.lot ? `Lot: ${wmsInventory.lot}` : 'No lot assigned'}
          </Text>
          <Text style={[componentStyles.wmsLotQtyText, wmsIsZero && componentStyles.wmsLotQtyTextZero]}>
            {wmsInventory.lot
              ? `On hand (this lot): ${wmsInventory.lot_quantity_on_hand}`
              : `On hand: ${wmsInventory.quantity_on_hand}`}
          </Text>
        </View>
      ) : null}
      {!!currentItem.instruction && (
        <DisplayInstructions item={currentItem} width={windowWidth} />
      )}
      {realTimeStatsEnabled && <DisplayLiveStats />}
      <View style={commonStyles.nextItemRowFlex}>
        <OrderItemSuggested
          item={currentItem}
          name={'suggestedItemSingle'}
          testID={'redirectToItem'}
          displayRow={swapped ? null : rowsWithSwapToggle}
          displayRowAtTop={swapped ? rowsWithSwapToggle : undefined}
          isSwap={swapped}
          getFontSize={() => getFontSize(windowWidth)}
          width={windowWidth}
          redirectToItemDetail={onItemPress}
          addBarcodeFun={(item) => dispatch(openAddBarcodeWindow(item))}
        />

        {renderDisplayImage()}
      </View>
    </>
  );
};

const componentStyles = StyleSheet.create({
  emptyStateContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  emptyStateText: {
    color: '#fff',
    fontSize: 18
  },
  mobileMenuOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000
  },
  swapToggle: {
    alignSelf: 'flex-end',
    paddingHorizontal: 8,
    paddingVertical: 4
  },
  locationRow: {
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 8,
    alignItems: 'center'
  },
  locationLabel: {
    fontSize: 18,
    paddingTop: 2,
    ...globalStyles.nextItemText
  },
  locationValue: {
    fontSize: 28,
    textAlign: 'left',
    ...globalStyles.nextItemText
  },
  wmsLotRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#2e4257',
    borderRadius: 4,
    marginHorizontal: 4,
    marginBottom: 4
  },
  wmsLotRowZero: {
    backgroundColor: '#b02a2a'
  },
  wmsLotText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold'
  },
  wmsLotQtyText: {
    color: '#fff',
    fontSize: 16
  },
  wmsLotQtyTextZero: {
    color: '#fff',
    fontWeight: 'bold'
  }
});

export default SuggestedItemDisplay;

